import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  Users,
  Wallet,
  Banknote,
  Building2,
  Landmark,
  Coins,
  Clock,
  UserPlus,
  TrendingUp,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  CheckCircle2,
  UserX,
} from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { Input } from '@/features/shared/components/ui/input';
import { cn } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useHasPermission } from '@/rbac/useRbac';
import {
  useEmployeePaymentMethods, useApprovePaymentMethod, useRejectPaymentMethod,
} from '@/features/payment-methods/api/hooks';
import type { PaymentMethod } from '@/lib/hrApi';
import { payrollApi } from '../api/payrollApi';
import type { ControlCenter, Currency, RiskAlert, AlertSeverity, AlertStatus } from '../types';
import {
  ALERT_SEVERITY_META,
  ALERT_SEVERITY_ORDER,
  ALERT_STATUS_META,
  ALERT_CATEGORY_LABEL,
} from '../lib/state';
import { formatMoney } from '../lib/format';
import { StatCard } from './StatCard';
import { StatusChip } from './StatusChip';
import { TaxTreatmentSection } from './TaxTreatmentSection';

/** Minimum override-reason length enforced by the backend (400 `override_reason_required`).
 *  Deliberately short — a brief reason like "NA" is allowed; we only require SOME reason. */
export const OVERRIDE_REASON_MIN = 2;

/** Alert rule codes whose "Resolve" opens a payout-method review instead of a status flip. */
const PAYMENT_RULE_CODES = ['no_payment_method', 'payment_method_pending_verification'];

interface ControlCenterTabProps {
  runId: string;
  /** Run currency — money figures on this tab always use it. */
  currency: Currency;
}

/** Pre-Payroll Control Center: gate banner + risk score, population/money/
 *  statutory/signals stat grids, the risk-alert worklist, and a readiness strip.
 *  The GET itself re-evaluates alerts server-side, so Refresh simply refetches. */
export const ControlCenterTab: React.FC<ControlCenterTabProps> = ({ runId, currency }) => {
  const { t } = useTranslation('payroll');
  const { canApprovePayroll } = useRoleAccess();
  const canApprovePayments = useHasPermission('employee:payment:approve');
  const navigate = useNavigate();
  const location = useLocation();

  // Open the employee profile so an operator can remediate an employee-scoped alert
  // (e.g. capture the missing statutory IDs). The run detail lives at
  // `<appBase>/payroll/runs/<id>`; strip that suffix to reach `<appBase>/employees/<id>`.
  const handleViewEmployee = (a: RiskAlert) => {
    if (!a.employeeId) return;
    // NOTE: we deliberately ignore a.actionLink. The backend emits it as an
    // app-relative link (e.g. `/employees/<id>/payment-details`), but it lacks
    // the `/t/<slug>/app` tenant prefix AND points at a sub-route that doesn't
    // exist — navigating to it raw doesn't match any route and falls through to
    // the dashboard. Resolve to the real, tenant-prefixed employee profile route.
    const appBase = location.pathname.replace(/\/payroll\/runs\/.*$/, '');
    navigate(`${appBase}/employees/${a.employeeId}`);
  };

  const [data, setData] = useState<ControlCenter | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busyAlertId, setBusyAlertId] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<RiskAlert | null>(null);
  const [paymentReview, setPaymentReview] = useState<RiskAlert | null>(null);

  // Payment alerts don't flip status on Resolve — an approver verifies the
  // pending payout method (or we deep-link to the alert's action page).
  const handleResolve = (a: RiskAlert) => {
    if (PAYMENT_RULE_CODES.includes(a.ruleCode)) {
      if (canApprovePayments && a.employeeId) { setPaymentReview(a); return; }
      if (a.actionLink) { navigate(a.actionLink); return; }
    }
    void changeAlert(a, 'resolved');
  };

  const load = useCallback(
    async (asRefresh = false) => {
      if (asRefresh) setRefreshing(true);
      try {
        const d = await payrollApi.getControlCenter(runId);
        setData(d);
        setFailed(false);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [runId],
  );

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  async function changeAlert(alert: RiskAlert, status: Exclude<AlertStatus, 'open'>, reason?: string) {
    setBusyAlertId(alert.id);
    try {
      await payrollApi.updateRiskAlert(alert.id, status, reason);
      toast.success(
        status === 'acknowledged'
          ? t('controlCenter.alertAcknowledged', 'Alert acknowledged')
          : status === 'resolved'
            ? t('controlCenter.alertResolved', 'Alert resolved')
            : t('controlCenter.alertOverridden', 'Alert overridden'),
      );
      setOverriding(null);
      // Refetch the whole rollup so the gate, risk score and counts stay in sync.
      await load(true);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      const msg =
        code === 'override_reason_required'
          ? t('controlCenter.overrideReasonRequired', 'An override reason is required.')
          : getApiErrorMessage(e, t('errors.generic', 'Something went wrong. Please try again.'));
      toast.error(getApiErrorMessage(e, msg), { duration: 6000 });
    } finally {
      setBusyAlertId(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t('controlCenter.loadFailed', 'Could not load the pre-payroll checks for this run.')}
        </p>
        <Button variant="outline" className="mt-4 gap-2" onClick={() => load()}>
          <RefreshCw className="h-4 w-4" />
          {t('controlCenter.retry', 'Try again')}
        </Button>
      </div>
    );
  }

  const openAlerts = data.alerts.filter((a) => a.status === 'open');
  const blockingCount = openAlerts.filter((a) => a.severity === 'critical' || a.severity === 'high').length;

  return (
    <div className="space-y-6">
      {/* Gate banner + refresh */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <GateBanner gate={data.gate} riskScore={data.riskScore} blockingCount={blockingCount} />
        {/* Re-reads the readiness checks. It deliberately changes NO money: a
            control labelled "refresh" that silently rewrote every employee's net
            pay would be a trap. Recalculating a validated run is a separate,
            named action on the run header — people reached for this one and
            concluded their approved changes had not applied, so the title says
            outright which job this button does and which it does not. */}
        <Button
          variant="outline"
          className="shrink-0 gap-2 self-start"
          disabled={refreshing}
          onClick={() => load(true)}
          title={t('controlCenter.refreshHelp', {
            defaultValue:
              'Re-runs the readiness checks against current data. It does not recalculate pay — use Recalculate on the run for that.',
          }) as string}
        >
          <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          {t('controlCenter.refresh', 'Refresh checks')}
        </Button>
      </div>

      {/* Population */}
      <Section title={t('controlCenter.population', 'Population')}>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label={t('controlCenter.popTotal', 'In this run')} value={String(data.population.total)} />
          <StatCard
            icon={CheckCircle2}
            label={t('controlCenter.popCalculated', 'Calculated')}
            value={String(data.population.calculated)}
            tone="success"
          />
          <StatCard
            icon={AlertTriangle}
            label={t('controlCenter.popErrors', 'Errors')}
            value={String(data.population.errors)}
            tone={data.population.errors > 0 ? 'danger' : 'default'}
            hint={data.population.errors > 0 ? t('controlCenter.popErrorsHint', 'These employees will not be paid') : undefined}
          />
          <StatCard icon={UserX} label={t('controlCenter.popExcluded', 'Excluded')} value={String(data.population.excluded)} />
        </div>
      </Section>

      {/* Money */}
      <Section title={t('controlCenter.money', 'Money')}>
        {data.totals ? (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <StatCard icon={Wallet} label={t('controlCenter.gross', 'Gross')} value={formatMoney(data.totals.gross, currency)} />
            <StatCard icon={Banknote} label={t('controlCenter.net', 'Net pay')} value={formatMoney(data.totals.net, currency)} tone="success" />
            <StatCard
              icon={Building2}
              label={t('controlCenter.employerCost', 'Employer cost')}
              value={formatMoney(data.totals.employerCost, currency)}
              tone="info"
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('controlCenter.totalsPending', 'Totals will appear once the run has been validated.')}
          </p>
        )}
      </Section>

      {/* Statutory — only the keys the country pack produced */}
      <StatutorySection statutory={data.statutoryTotals} currency={currency} />

      {/* Changes & signals */}
      <Section title={t('controlCenter.changesSignals', 'Changes & signals')}>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <StatCard icon={UserPlus} label={t('controlCenter.newHires', 'New hires')} value={String(data.changes.newHires)} />
          <StatCard icon={TrendingUp} label={t('controlCenter.salaryChanges', 'Salary changes')} value={String(data.changes.salaryChanges)} />
          <StatCard
            icon={Coins}
            label={t('controlCenter.activeLoans', 'Active loans')}
            value={String(data.loans.activeCount)}
            hint={t('controlCenter.expectedDeductions', 'Expected deductions: {{amount}}', {
              amount: formatMoney(data.loans.expectedDeductions, currency),
            })}
          />
          {data.attendance && (
            <>
              <StatCard
                icon={Clock}
                label={t('controlCenter.unapprovedOt', 'Unapproved overtime')}
                value={String(data.attendance.unapprovedOvertime)}
                tone={data.attendance.unapprovedOvertime > 0 ? 'danger' : 'default'}
              />
              <StatCard
                icon={UserX}
                label={t('controlCenter.paidZeroAttendance', 'Paid with zero attendance')}
                value={String(data.attendance.paidWithZeroAttendance)}
                tone={data.attendance.paidWithZeroAttendance > 0 ? 'danger' : 'default'}
              />
              <StatCard
                icon={AlertTriangle}
                label={t('controlCenter.openCriticalExceptions', 'Open critical exceptions')}
                value={String(data.attendance.openCriticalExceptions)}
                tone={data.attendance.openCriticalExceptions > 0 ? 'danger' : 'default'}
                hint={t('controlCenter.attendanceHint', 'Review them on the Attendance page')}
              />
            </>
          )}
        </div>
      </Section>

      {/* ME7: employee + contractor of this company — HIGH, non-blocking, confirmed on the engagements */}
      <TaxTreatmentSection
        section={data.taxTreatment}
        onViewPerson={(personId) => {
          const appBase = location.pathname.replace(/\/payroll\/runs\/.*$/, '');
          navigate(`${appBase}/employees/${personId}`);
        }}
      />

      {/* Alerts worklist */}
      <Section
        title={t('controlCenter.alerts', 'Risk alerts')}
        aside={
          openAlerts.length > 0 ? (
            <Badge variant="warning" dot>
              {t('controlCenter.openCount', '{{count}} open', { count: openAlerts.length })}
            </Badge>
          ) : undefined
        }
      >
        <AlertsList
          alerts={data.alerts}
          canAct={canApprovePayroll}
          busyAlertId={busyAlertId}
          onAcknowledge={(a) => changeAlert(a, 'acknowledged')}
          onResolve={handleResolve}
          onOverride={(a) => setOverriding(a)}
          onViewEmployee={handleViewEmployee}
        />
      </Section>

      {/* Readiness strip */}
      <ReadinessStrip readiness={data.readiness} />

      {/* Override dialog */}
      <OverrideAlertDialog
        alert={overriding}
        busy={!!overriding && busyAlertId === overriding.id}
        onCancel={() => setOverriding(null)}
        onConfirm={(reason) => overriding && changeAlert(overriding, 'overridden', reason)}
      />

      {/* Payout-method review (payment alerts) */}
      <PaymentReviewDialog
        alert={paymentReview}
        onClose={() => setPaymentReview(null)}
        onDone={() => load(true)}
      />
    </div>
  );
};

// --- payout-method review dialog (payment alerts) --------------------------------

const PAYMENT_STATUS_META: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  PENDING_VERIFICATION: { label: 'Pending Verification', variant: 'warning' },
  APPROVED: { label: 'Approved', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'destructive' },
  INACTIVE: { label: 'Inactive', variant: 'secondary' },
};

const describeMethod = (m: PaymentMethod): string =>
  m.type === 'BANK'
    ? [m.bank_name, m.account_masked].filter(Boolean).join(' ') || 'Bank account'
    : [m.mobile_money_provider, m.phone_masked].filter(Boolean).join(' ') || 'Mobile money';

const PaymentReviewDialog: React.FC<{
  alert: RiskAlert | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ alert, onClose, onDone }) => {
  const { t } = useTranslation('payroll');
  const { data, isLoading } = useEmployeePaymentMethods(alert?.employeeId);
  const approve = useApprovePaymentMethod();
  const reject = useRejectPaymentMethod();
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => { setRejectId(null); setReason(''); }, [alert?.id]);

  const methods = data ?? [];
  const busy = approve.isPending || reject.isPending;

  const doApprove = async (id: string) => {
    try {
      await approve.mutateAsync(id);
      toast.success(t('controlCenter.paymentApproved', 'Payout method approved'));
      onDone();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('errors.generic', 'Something went wrong. Please try again.')), { duration: 6000 });
    }
  };

  const doReject = async () => {
    if (!rejectId || !reason.trim()) return;
    try {
      await reject.mutateAsync({ id: rejectId, reason: reason.trim() });
      toast.success(t('controlCenter.paymentRejected', 'Payout method rejected'));
      setRejectId(null); setReason('');
      onDone();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('errors.generic', 'Something went wrong. Please try again.')), { duration: 6000 });
    }
  };

  return (
    <Dialog open={!!alert} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('controlCenter.paymentReviewTitle', 'Review payout method')}</DialogTitle>
          <DialogDescription>
            {t('controlCenter.paymentReviewDesc', 'Approve or reject this employee’s payout method so payroll can pay them.')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> {t('controlCenter.loading', 'Loading…')}
          </div>
        ) : methods.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('controlCenter.paymentReviewNone', 'This employee has no payout method on file yet.')}
          </p>
        ) : (
          <div className="space-y-3">
            {methods.map((m) => {
              const meta = PAYMENT_STATUS_META[m.status] ?? PAYMENT_STATUS_META.INACTIVE;
              return (
                <div key={m.id} className="rounded-xl border border-border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal">{describeMethod(m)}</p>
                      {m.account_holder_name && (
                        <p className="text-xs text-muted-foreground">{m.account_holder_name}</p>
                      )}
                    </div>
                    <Badge variant={meta.variant}>{meta.label}</Badge>
                  </div>
                  {m.status === 'PENDING_VERIFICATION' && (
                    rejectId === m.id ? (
                      <div className="mt-3 space-y-2">
                        <Input
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder={t('controlCenter.paymentRejectReason', 'Reason for rejection (required)')}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="destructive" disabled={busy || !reason.trim()} onClick={doReject}>
                            {t('controlCenter.confirmReject', 'Confirm reject')}
                          </Button>
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => { setRejectId(null); setReason(''); }}>
                            {t('controlCenter.cancel', 'Cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" disabled={busy} onClick={() => doApprove(m.id)}>
                          {t('controlCenter.approve', 'Approve')}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-500/25"
                          disabled={busy} onClick={() => { setRejectId(m.id); setReason(''); }}>
                          {t('controlCenter.reject', 'Reject')}
                        </Button>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('controlCenter.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- gate banner + risk gauge ------------------------------------------------

const riskBand = (score: number): 'ready' | 'warning' | 'blocked' =>
  score < 20 ? 'ready' : score < 50 ? 'warning' : 'blocked';

// Theme tokens so the gate banner + risk ring match the app in both light and dark. `fg`
// is a CSS colour (the ring stroke); `bg`/`text` are utility classes.
const TONE = {
  ready: { fg: 'var(--color-success)', bg: 'bg-success-soft border-success/20', text: 'text-success' },
  warning: { fg: 'var(--color-warning)', bg: 'bg-warning-soft border-warning/20', text: 'text-warning' },
  blocked: { fg: 'var(--color-danger)', bg: 'bg-danger-soft border-danger/20', text: 'text-danger' },
} as const;

const GateBanner: React.FC<{
  gate: ControlCenter['gate'];
  riskScore: number;
  blockingCount: number;
}> = ({ gate, riskScore, blockingCount }) => {
  const { t } = useTranslation('payroll');
  const tone = TONE[gate.status];
  const Icon = gate.status === 'ready' ? ShieldCheck : gate.status === 'warning' ? AlertTriangle : ShieldAlert;
  const title =
    gate.status === 'ready'
      ? t('controlCenter.gateReady', 'Ready to pay')
      : gate.status === 'warning'
        ? t('controlCenter.gateWarning', 'Warnings — review recommended')
        : t('controlCenter.gateBlocked', 'Blocked — {{count}} critical/high alerts open', { count: blockingCount });

  return (
    <div className={cn('flex flex-1 items-center gap-4 rounded-xl border p-4', tone.bg)}>
      <Icon className={cn('h-6 w-6 shrink-0', tone.text)} />
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-semibold', tone.text)}>{title}</p>
        {gate.blocking.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {gate.blocking.map((b, i) => (
              <li key={i}>• {b}</li>
            ))}
          </ul>
        )}
      </div>
      <RiskGauge score={riskScore} />
    </div>
  );
};

/** Compact 0–100 risk gauge: an SVG ring coloured by band (<20 green, <50 amber, ≥50 red). */
const RiskGauge: React.FC<{ score: number }> = ({ score }) => {
  const { t } = useTranslation('payroll');
  const color = TONE[riskBand(score)].fg;
  const r = 20;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex shrink-0 flex-col items-center" title={t('controlCenter.riskScoreTitle', 'Risk score (0–100)')}>
      <svg width="52" height="52" viewBox="0 0 52 52" role="img" aria-label={`Risk ${score}/100`}>
        <circle cx="26" cy="26" r={r} fill="none" stroke="currentColor" strokeWidth="5" className="text-border" />
        <circle
          cx="26"
          cy="26"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${(score / 100) * c} ${c}`}
          transform="rotate(-90 26 26)"
        />
        <text x="26" y="30" textAnchor="middle" fontSize="13" fontWeight="700" fill={color}>
          {score}
        </text>
      </svg>
      <span className="mt-0.5 text-[10px] font-medium text-muted-foreground">
        {t('controlCenter.riskScore', 'Risk score')}
      </span>
    </div>
  );
};

// --- section wrapper -----------------------------------------------------------

const Section: React.FC<{ title: string; aside?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  aside,
  children,
}) => (
  <section>
    <div className="mb-3 flex items-center gap-3">
      <h3 className="text-sm font-semibold text-charcoal">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

// --- statutory ------------------------------------------------------------------

const StatutorySection: React.FC<{ statutory: ControlCenter['statutoryTotals']; currency: Currency }> = ({
  statutory,
  currency,
}) => {
  const { t } = useTranslation('payroll');
  const rows: Array<{ key: string; label: string; amount: number }> = [];
  if (statutory.paye != null) rows.push({ key: 'paye', label: t('controlCenter.paye', 'PAYE'), amount: statutory.paye });
  if (statutory.nssfEmployee != null)
    rows.push({ key: 'nssfEe', label: t('controlCenter.nssfEmployee', 'NSSF (employee)'), amount: statutory.nssfEmployee });
  if (statutory.nssfEmployer != null)
    rows.push({ key: 'nssfEr', label: t('controlCenter.nssfEmployer', 'NSSF (employer)'), amount: statutory.nssfEmployer });
  if (statutory.sdl != null) rows.push({ key: 'sdl', label: t('controlCenter.sdl', 'SDL'), amount: statutory.sdl });
  if (statutory.wcf != null) rows.push({ key: 'wcf', label: t('controlCenter.wcf', 'WCF'), amount: statutory.wcf });
  if (statutory.heslb != null) rows.push({ key: 'heslb', label: t('controlCenter.heslb', 'HESLB'), amount: statutory.heslb });
  if (rows.length === 0) return null;

  return (
    <Section title={t('controlCenter.statutory', 'Statutory')}>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {rows.map((r) => (
          <StatCard key={r.key} icon={Landmark} label={r.label} value={formatMoney(r.amount, currency)} tone="info" />
        ))}
      </div>
    </Section>
  );
};

// --- alerts list -------------------------------------------------------------------

const AlertsList: React.FC<{
  alerts: RiskAlert[];
  canAct: boolean;
  busyAlertId: string | null;
  onAcknowledge: (a: RiskAlert) => void;
  onResolve: (a: RiskAlert) => void;
  onOverride: (a: RiskAlert) => void;
  onViewEmployee: (a: RiskAlert) => void;
}> = ({ alerts, canAct, busyAlertId, onAcknowledge, onResolve, onOverride, onViewEmployee }) => {
  const { t } = useTranslation('payroll');

  if (alerts.length === 0) {
    return (
      <div className="rounded-xl border border-success/20 bg-success-soft p-4 text-sm text-success">
        {t('controlCenter.noAlerts', 'No risk alerts — this run looks clean.')}
      </div>
    );
  }

  const groups = ALERT_SEVERITY_ORDER.map((sev) => ({
    severity: sev,
    items: alerts.filter((a) => a.severity === sev),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.severity}>
          <div className="mb-2 flex items-center gap-2">
            <StatusChip meta={ALERT_SEVERITY_META[group.severity]} />
            <span className="text-xs text-muted-foreground">
              {t('controlCenter.alertCount', '{{count}} alert(s)', { count: group.items.length })}
            </span>
          </div>
          <div className="space-y-2">
            {group.items.map((a) => (
              <AlertRow
                key={a.id}
                alert={a}
                canAct={canAct}
                busy={busyAlertId === a.id}
                onAcknowledge={() => onAcknowledge(a)}
                onResolve={() => onResolve(a)}
                onOverride={() => onOverride(a)}
                onViewEmployee={() => onViewEmployee(a)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AlertRow: React.FC<{
  alert: RiskAlert;
  canAct: boolean;
  busy: boolean;
  onAcknowledge: () => void;
  onResolve: () => void;
  onOverride: () => void;
  onViewEmployee: () => void;
}> = ({ alert, canAct, busy, onAcknowledge, onResolve, onOverride, onViewEmployee }) => {
  const { t } = useTranslation('payroll');
  const actionable = alert.status === 'open' || alert.status === 'acknowledged';

  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{ALERT_CATEGORY_LABEL[alert.category] ?? alert.category}</Badge>
            <StatusChip meta={ALERT_STATUS_META[alert.status]} />
            <span className="text-[11px] font-medium text-muted-foreground">{alert.ruleCode}</span>
          </div>
          <p className="mt-1.5 text-sm text-charcoal">{alert.message}</p>
          {/* Only suggest an action while the alert is still actionable — a resolved/overridden
              alert showing "Verify the payout method" reads as if it were still pending. */}
          {alert.recommendedAction && actionable && (
            <p className="mt-0.5 text-xs text-muted-foreground">{alert.recommendedAction}</p>
          )}
          {alert.status === 'overridden' && alert.overrideReason && (
            <p className="mt-0.5 text-xs italic text-muted-foreground">
              {t('controlCenter.overriddenBecause', 'Override reason: {{reason}}', { reason: alert.overrideReason })}
            </p>
          )}
        </div>
        {(alert.employeeId || (canAct && actionable)) && (
          <div className="flex shrink-0 flex-wrap gap-2">
            {/* Reach the employee profile to actually remediate a per-employee alert
                (e.g. capture the missing statutory IDs) — the alert row is otherwise a dead end. */}
            {alert.employeeId && (
              <Button size="sm" disabled={busy} onClick={onViewEmployee}>
                {t('controlCenter.viewEmployee', 'View employee')}
              </Button>
            )}
            {canAct && actionable && (
              <>
                {alert.status === 'open' && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={onAcknowledge}>
                    {t('controlCenter.acknowledge', 'Acknowledge')}
                  </Button>
                )}
                <Button size="sm" variant="outline" disabled={busy} onClick={onResolve}>
                  {t('controlCenter.resolve', 'Resolve')}
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={onOverride}>
                  {t('controlCenter.override', 'Override')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- readiness strip -------------------------------------------------------------

const ReadinessStrip: React.FC<{ readiness: ControlCenter['readiness'] }> = ({ readiness }) => {
  const { t } = useTranslation('payroll');
  const cells: Array<{ label: string; value: number; variant: 'success' | 'warning' | 'danger' | 'secondary' }> = [
    { label: t('controlCenter.ready', 'Ready'), value: readiness.ready, variant: 'success' },
    { label: t('controlCenter.warning', 'Warning'), value: readiness.warning, variant: 'warning' },
    { label: t('controlCenter.blocked', 'Blocked'), value: readiness.blocked, variant: 'danger' },
    { label: t('controlCenter.excluded', 'Excluded'), value: readiness.excluded, variant: 'secondary' },
  ];
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="text-sm font-semibold text-charcoal">
          {t('controlCenter.readiness', 'Employee readiness')}
        </span>
        {cells.map((c) => (
          <span key={c.label} className="inline-flex items-center gap-1.5 text-sm text-charcoal">
            <Badge variant={c.variant} dot>
              {c.value}
            </Badge>
            {c.label}
          </span>
        ))}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">
        {t(
          'controlCenter.readinessHint',
          'Per-employee details are on the readiness card in the employee directory.',
        )}
      </p>
    </div>
  );
};

// --- override dialog ------------------------------------------------------------

const OverrideAlertDialog: React.FC<{
  alert: RiskAlert | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}> = ({ alert, busy, onCancel, onConfirm }) => {
  const { t } = useTranslation('payroll');
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [alert?.id]);
  const tooShort = reason.trim().length < OVERRIDE_REASON_MIN;

  return (
    <Dialog open={!!alert} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('controlCenter.overrideTitle', 'Override this alert?')}</DialogTitle>
          <DialogDescription>
            {t(
              'controlCenter.overrideDesc',
              'Overriding records that you accept this risk. Your reason is kept on the audit trail.',
            )}
          </DialogDescription>
        </DialogHeader>
        {alert && <p className="text-sm text-charcoal">{alert.message}</p>}
        <Textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('controlCenter.overrideReasonPlaceholder', 'Why is it safe to proceed? (a brief reason, e.g. NA)')}
        />
        {tooShort && reason.length > 0 && (
          <p className="text-xs text-danger">
            {t('controlCenter.overrideReasonTooShort', 'Please enter a reason.')}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            {t('controlCenter.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" disabled={busy || tooShort} onClick={() => onConfirm(reason.trim())}>
            {t('controlCenter.confirmOverride', 'Override alert')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
